import {
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  type Ref,
  type ShallowRef,
} from 'vue';

/**
 * Extracts the CSS custom-property name from a simple `var(--xxx)` string.
 * Does not handle fallback values — Vanilla Extract contracts never include them.
 */
function extractCssVarName(value: string): string | null {
  const match = value.match(/^var\((--[^,)]+)\)$/);
  return match ? match[1].trim() : null;
}

/**
 * Resolves a CSS variable reference to its computed value on the given element.
 * Returns the input unchanged when it is not a `var(--xxx)` reference.
 */
export function resolveCssVar(el: Element, cssVarRef: string): string {
  const prop = extractCssVarName(cssVarRef);
  if (!prop) return cssVarRef;
  return getComputedStyle(el).getPropertyValue(prop).trim();
}

/**
 * Watches for class attribute changes on `document.body` (theme switches)
 * and calls the provided callback to re-resolve CSS variables.
 */
function useThemeChangeObserver(callback: () => void) {
  let observer: MutationObserver | null = null;

  onMounted(() => {
    observer = new MutationObserver(callback);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  });

  onBeforeUnmount(() => {
    observer?.disconnect();
    observer = null;
  });
}

/**
 * Resolves an ordered list of CSS var refs to actual color strings.
 * Re-resolves whenever the container ref changes or the theme switches.
 */
export function useResolvedCssVars(
  containerRef: Ref<HTMLElement | null> | ShallowRef<HTMLElement | null>,
  cssVarRefs: readonly string[],
): Ref<string[]> {
  const resolved = ref<string[]>([]);

  function resolve() {
    const el = containerRef.value;
    if (!el) return;
    resolved.value = cssVarRefs.map((v) => resolveCssVar(el, v));
  }

  onMounted(resolve);
  watch(containerRef, resolve);
  useThemeChangeObserver(resolve);

  return resolved;
}

/**
 * Resolves a keyed record of CSS var refs to actual color strings.
 * Re-resolves whenever the container ref changes or the theme switches.
 */
export function useResolvedCssVarRecord<K extends string>(
  containerRef: Ref<HTMLElement | null> | ShallowRef<HTMLElement | null>,
  cssVarRecord: Record<K, string>,
): Ref<Record<K, string>> {
  const keys = Object.keys(cssVarRecord) as K[];
  const resolved = ref({} as Record<K, string>) as Ref<Record<K, string>>;

  function resolve() {
    const el = containerRef.value;
    if (!el) return;
    const result = {} as Record<K, string>;
    for (const key of keys) {
      result[key] = resolveCssVar(el, cssVarRecord[key]);
    }
    resolved.value = result;
  }

  onMounted(resolve);
  watch(containerRef, resolve);
  useThemeChangeObserver(resolve);

  return resolved;
}
